import { Alert, Badge, Button, Field, Select } from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AlertTriangle, ArrowLeft, CheckCircle2, FileUp, Upload, XCircle } from 'lucide-react'
import { useMemo, useState } from 'react'

import { errorMessage } from '@/api/errors'
import { useImportGymMembers } from '@/api/queries'
import type { GymMemberImportResult, GymMemberImportRow } from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { readCsvFile, type ParsedCsv } from '@/lib/csv'

/**
 * Import delle anagrafiche da CSV — ANA-07.
 *
 * ## Tre passi, non un pulsante
 *
 * **Il file non arriva al server**: lo legge il browser e manda righe già interpretate. Non
 * è un dettaglio di trasporto, è ciò che rende possibili i tre passi — si scegle il file, si
 * *mappa* ogni colonna su un campo, si guarda l'anteprima — e i tre passi sono l'unica forma
 * che funziona su file veri: la colonna «Cod.Fisc.» di un gestionale e «codice fiscale» di
 * un altro sono lo stesso campo, e chi lo sa è chi sta guardando il file.
 *
 * Il rovescio è che separatore, codifica e formati vanno indovinati qui. Si indovinano e si
 * lasciano correggere, perché i file veri arrivano da Excel italiano: `;` come separatore,
 * `31/12/1990` come data.
 *
 * ## L'anteprima non è una validazione, è una revisione
 *
 * Il `dryRun` è il **primo dei due invii** e non un'opzione: il report torna una voce per
 * riga con l'esito e — per chi è stato riconosciuto — l'id e il criterio. La domanda a cui
 * la schermata deve far rispondere è «queste dodici esistono già, sono queste persone?»,
 * perché un import di anagrafiche sbagliato non si annulla: le righe create sono persone, e
 * cancellarle è ANA-09 una per una.
 *
 * ⚠️ **Due righe sono la stessa persona per codice fiscale o numero di tessera, mai per nome
 * e cognome.** In una palestra ci sono due Rossi Marco. Una riga senza nessuno dei due crea
 * sempre, quindi reimportare lo stesso file senza quelle colonne **raddoppia** gli iscritti:
 * l'avvertenza sta nella pagina, prima dell'invio, non in questo commento.
 */
export const Route = createFileRoute('/_app/iscritti/import')({
  component: ImportPage,
})

/** I campi che l'import accetta, con l'etichetta da mostrare nel selettore. */
const FIELDS = [
  { key: 'firstName', label: 'Nome', required: true },
  { key: 'lastName', label: 'Cognome', required: true },
  { key: 'memberNumber', label: 'Numero di tessera', required: false },
  { key: 'taxCode', label: 'Codice fiscale', required: false },
  { key: 'bornOn', label: 'Data di nascita', required: false },
  { key: 'birthPlace', label: 'Luogo di nascita', required: false },
  { key: 'birthProvince', label: 'Provincia di nascita', required: false },
  { key: 'sex', label: 'Sesso', required: false },
  { key: 'email', label: 'Email', required: false },
  { key: 'phone', label: 'Telefono', required: false },
  { key: 'addressLine', label: 'Indirizzo', required: false },
  { key: 'postalCode', label: 'CAP', required: false },
  { key: 'city', label: 'Città', required: false },
  { key: 'province', label: 'Provincia', required: false },
  { key: 'internalNotes', label: 'Note interne', required: false },
] as const

type FieldKey = (typeof FIELDS)[number]['key']

/** «Ignora» in cima: è ciò che resta su ogni colonna che l'accoppiamento non riconosce. */
type ColumnChoice = FieldKey | ''

const COLUMN_OPTIONS: { value: ColumnChoice; label: string }[] = [
  { value: '', label: 'Ignora' },
  ...FIELDS.map((field) => ({
    value: field.key satisfies ColumnChoice,
    label: field.required ? `${field.label} *` : field.label,
  })),
]

const DUPLICATE_OPTIONS: { value: 'skip' | 'update'; label: string }[] = [
  { value: 'skip', label: 'Lascia stare' },
  { value: 'update', label: 'Aggiorna i campi presenti nel file' },
]

/**
 * Accoppiamento automatico fra intestazione e campo.
 *
 * Copre le forme con cui i gestionali italiani scrivono le stesse cose. Serve a **proporre**,
 * non a decidere: ogni accoppiamento resta modificabile, perché indovinare bene nove volte su
 * dieci e sbagliare in silenzio la decima è peggio che chiedere.
 */
const ALIASES: Record<string, FieldKey> = {
  nome: 'firstName',
  cognome: 'lastName',
  'numero tessera': 'memberNumber',
  tessera: 'memberNumber',
  'n. tessera': 'memberNumber',
  matricola: 'memberNumber',
  'codice fiscale': 'taxCode',
  'cod.fisc.': 'taxCode',
  codfisc: 'taxCode',
  cf: 'taxCode',
  'data di nascita': 'bornOn',
  'data nascita': 'bornOn',
  nato: 'bornOn',
  'luogo di nascita': 'birthPlace',
  'comune di nascita': 'birthPlace',
  sesso: 'sex',
  email: 'email',
  'e-mail': 'email',
  posta: 'email',
  telefono: 'phone',
  cellulare: 'phone',
  tel: 'phone',
  indirizzo: 'addressLine',
  via: 'addressLine',
  cap: 'postalCode',
  citta: 'city',
  città: 'city',
  comune: 'city',
  provincia: 'province',
  prov: 'province',
  note: 'internalNotes',
}

/**
 * Converte una data scritta come si scrive in Italia.
 *
 * `31/12/1990` e `31-12-1990` diventano `1990-12-31`; una data già ISO passa intatta;
 * qualunque altra cosa passa **così com'è** e la fa fallire il server, riga per riga, con il
 * campo colpevole. Correggerla a indovinare sarebbe peggio: `01/02/1990` in un file inglese
 * è il 2 gennaio, e non c'è modo di saperlo da qui.
 */
function normalizeDay(value: string): string {
  const trimmed = value.trim()
  const italian = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/.exec(trimmed)
  if (italian === null) return trimmed
  const [, day, month, year] = italian
  return `${year}-${month!.padStart(2, '0')}-${day!.padStart(2, '0')}`
}

function normalizeSex(value: string): string {
  const first = value.trim().toLowerCase()[0]
  if (first === 'f') return 'female'
  if (first === 'm') return 'male'
  return value.trim()
}

function ImportPage() {
  const [parsed, setParsed] = useState<ParsedCsv | null>(null)
  const [mapping, setMapping] = useState<Record<number, ColumnChoice>>({})
  const [onDuplicate, setOnDuplicate] = useState<'skip' | 'update'>('skip')
  const [preview, setPreview] = useState<GymMemberImportResult | null>(null)
  const [applied, setApplied] = useState<GymMemberImportResult | null>(null)
  const [readError, setReadError] = useState<string | null>(null)

  const importer = useImportGymMembers()

  async function onFile(file: File): Promise<void> {
    setPreview(null)
    setApplied(null)
    setReadError(null)
    const result = await readCsvFile(file)
    if ('error' in result) {
      setReadError(result.error)
      return
    }
    setParsed(result)
    // L'accoppiamento proposto: sull'intestazione ripulita da spazi e maiuscole.
    const guessed: Record<number, ColumnChoice> = {}
    result.headers.forEach((header, index) => {
      guessed[index] = ALIASES[header.toLowerCase()] ?? ''
    })
    setMapping(guessed)
  }

  const mapped = useMemo((): GymMemberImportRow[] => {
    if (parsed === null) return []
    const columns = Object.entries(mapping).filter(([, field]) => field !== '')

    return parsed.rows.map((cells, rowIndex) => {
      // `line` è la riga **nel file**: +2 perché l'intestazione è la prima e si conta da uno.
      // Senza, il report dice «riga 3» e l'operatore cerca la riga sbagliata.
      const row: Record<string, unknown> = { line: rowIndex + 2 }
      for (const [rawIndex, field] of columns) {
        const value = cells[Number(rawIndex)] ?? ''
        if (value.trim() === '') continue
        if (field === 'bornOn') row[field] = normalizeDay(value)
        else if (field === 'sex') row[field] = normalizeSex(value)
        else row[field] = value.trim()
      }
      return row as unknown as GymMemberImportRow
    })
  }, [parsed, mapping])

  const missingRequired = FIELDS.filter(
    (field) => field.required && !Object.values(mapping).includes(field.key),
  )
  const hasIdentity = Object.values(mapping).some(
    (field) => field === 'taxCode' || field === 'memberNumber',
  )
  // Il server ne accetta 500 per richiesta: oltre, il file va spezzato. Il pannello non lo
  // spezza da sé ancora, e dirlo prima è meglio che un `422` dopo.
  const tooLarge = mapped.length > 500

  async function run(dryRun: boolean): Promise<void> {
    const result = await importer.mutateAsync({ members: mapped, dryRun, onDuplicate })
    if (dryRun) setPreview(result)
    else setApplied(result)
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Importa iscritti"
        description="Da un file CSV esportato da un altro gestionale."
        actions={
          <Button variant="ghost" asChild>
            <Link to="/iscritti">
              <ArrowLeft className="size-4" />
              Torna all’elenco
            </Link>
          </Button>
        }
      />

      {/* Passo 1 — il file */}
      <section className="bg-card rounded-lg border p-4">
        <h2 className="font-semibold">1. Il file</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Il file <strong>non viene caricato</strong>: lo legge questo browser e al server arrivano
          solo le righe, già accoppiate ai campi. Un file di anagrafiche è un blocco di dati
          personali, e non caricarlo significa non lasciarne copia da nessuna parte.
        </p>
        <label className="mt-3 flex w-fit cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm">
          <FileUp className="size-4" />
          <span>{parsed?.filename ?? 'Scegli un CSV…'}</span>
          <input
            type="file"
            accept=".csv,text/csv,text/plain"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) void onFile(file)
            }}
          />
        </label>
        {readError !== null ? (
          <Alert variant="danger" className="mt-3">
            {readError}
          </Alert>
        ) : null}
        {parsed !== null ? (
          <p className="text-muted-foreground mt-2 text-xs">
            {mapped.length} righe, separatore{' '}
            <code>{parsed.separator === '\t' ? 'tabulazione' : parsed.separator}</code>.
          </p>
        ) : null}
      </section>

      {/* Passo 2 — la mappatura */}
      {parsed !== null ? (
        <section className="bg-card rounded-lg border p-4">
          <h2 className="font-semibold">2. Le colonne</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            L’accoppiamento è <strong>proposto</strong>, non deciso: controllalo. Le colonne
            lasciate su «Ignora» non vengono importate.
          </p>

          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {parsed.headers.map((header, index) => (
              <Field
                key={`${header}-${index}`}
                label={header === '' ? `Colonna ${index + 1}` : header}
                hint={`Es.: ${parsed.rows[0]?.[index] ?? '—'}`}
              >
                {(props) => (
                  <Select
                    {...props}
                    value={mapping[index] ?? ''}
                    onValueChange={(value) =>
                      setMapping((current) => ({ ...current, [index]: value as ColumnChoice }))
                    }
                    items={COLUMN_OPTIONS}
                  />
                )}
              </Field>
            ))}
          </div>

          {missingRequired.length > 0 ? (
            <Alert variant="danger" className="mt-3">
              Manca l’accoppiamento di: {missingRequired.map((f) => f.label).join(', ')}. Nome e
              cognome sono gli unici campi obbligatori.
            </Alert>
          ) : null}

          {!hasIdentity ? (
            <Alert variant="warning" className="mt-3">
              <AlertTriangle className="size-4" />
              <span>
                Nessuna colonna è accoppiata a <strong>codice fiscale</strong> o{' '}
                <strong>numero di tessera</strong>: senza uno dei due, ogni riga crea una persona
                nuova. Nome e cognome non bastano a riconoscere qualcuno — in una palestra ci sono
                due Rossi Marco — quindi reimportare questo file <strong>raddoppierebbe</strong> gli
                iscritti.
              </span>
            </Alert>
          ) : null}

          <div className="mt-4 flex flex-wrap items-end gap-3">
            <Field
              label="Chi c’è già"
              hint="Riconosciuto per codice fiscale o numero di tessera."
              className="w-64"
            >
              {(props) => (
                <Select
                  {...props}
                  value={onDuplicate}
                  onValueChange={(value) => setOnDuplicate(value as typeof onDuplicate)}
                  items={DUPLICATE_OPTIONS}
                />
              )}
            </Field>
            <Button
              onClick={() => void run(true)}
              disabled={missingRequired.length > 0 || tooLarge}
              loading={importer.isPending}
            >
              Anteprima
            </Button>
          </div>

          {tooLarge ? (
            <Alert variant="danger" className="mt-3">
              Il file ha {mapped.length} righe e il massimo per invio è 500. Dividilo in più file.
            </Alert>
          ) : null}
          {importer.isError ? (
            <Alert variant="danger" className="mt-3">
              {errorMessage(importer.error)}
            </Alert>
          ) : null}
        </section>
      ) : null}

      {/* Passo 3 — l'anteprima, e solo dopo la scrittura */}
      {preview !== null && applied === null ? (
        <section className="bg-card rounded-lg border p-4">
          <h2 className="font-semibold">3. Controlla, poi importa</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Niente è stato scritto. Le righe già presenti sono elencate qui sotto:{' '}
            <strong>sono queste le persone che ti aspetti?</strong>
          </p>
          <ResultSummary result={preview} />
          <RowTable result={preview} />

          <div className="mt-4 flex items-center gap-3">
            <Button onClick={() => void run(false)} loading={importer.isPending}>
              <Upload className="size-4" />
              Importa {preview.created + preview.updated} righe
            </Button>
            <span className="text-muted-foreground text-xs">
              Le anagrafiche create non si eliminano: si anonimizzano una per una.
            </span>
          </div>
        </section>
      ) : null}

      {applied !== null ? (
        <section className="bg-card rounded-lg border p-4">
          <h2 className="font-semibold">Importazione eseguita</h2>
          <ResultSummary result={applied} />
          <RowTable result={applied} />
          <Button variant="ghost" asChild className="mt-4">
            <Link to="/iscritti">Vai all’elenco</Link>
          </Button>
        </section>
      ) : null}
    </div>
  )
}

function ResultSummary({ result }: { result: GymMemberImportResult }) {
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      <Badge variant="success">{result.created} da creare</Badge>
      <Badge variant="secondary">{result.updated} da aggiornare</Badge>
      <Badge variant="secondary">{result.skipped} già presenti</Badge>
      {result.failed > 0 ? <Badge variant="danger">{result.failed} da correggere</Badge> : null}
    </div>
  )
}

/**
 * Le righe con un esito che richiede attenzione, **prima** quelle fallite.
 *
 * Non tutte: un file di cinquecento righe tutte nuove non ha niente da rivedere, e mostrarle
 * seppellirebbe le dodici che contano. Chi vuole vederle tutte guarda il file.
 */
function RowTable({ result }: { result: GymMemberImportResult }) {
  const interesting = result.rows
    .filter((row) => row.outcome !== 'created')
    .sort((a, b) => (a.outcome === 'failed' ? -1 : b.outcome === 'failed' ? 1 : 0))

  if (interesting.length === 0) {
    return (
      <p className="text-muted-foreground mt-3 text-sm">
        Tutte le righe sono persone nuove: niente da rivedere.
      </p>
    )
  }

  return (
    <ul className="mt-3 divide-y rounded-md border text-sm">
      {interesting.slice(0, 100).map((row) => (
        <li key={row.index} className="flex items-start gap-2 p-2">
          {row.outcome === 'failed' ? (
            <XCircle className="text-danger-content mt-0.5 size-4 shrink-0" />
          ) : (
            <CheckCircle2 className="text-muted-foreground mt-0.5 size-4 shrink-0" />
          )}
          <div className="min-w-0">
            <p>
              Riga {row.line ?? row.index + 1} ·{' '}
              {row.outcome === 'failed'
                ? 'da correggere'
                : row.outcome === 'skipped'
                  ? 'già presente, lasciata stare'
                  : 'già presente, da aggiornare'}
              {row.matchedBy !== null ? (
                <span className="text-muted-foreground">
                  {' '}
                  (riconosciuta per{' '}
                  {row.matchedBy === 'tax_code' ? 'codice fiscale' : 'numero di tessera'})
                </span>
              ) : null}
            </p>
            {row.message !== null ? (
              <p className="text-muted-foreground text-xs">{row.message}</p>
            ) : null}
            {row.gymMemberId !== null ? (
              <Link
                to="/iscritti/$gymMemberId"
                params={{ gymMemberId: row.gymMemberId }}
                className="text-primary-content text-xs underline"
              >
                Apri la scheda
              </Link>
            ) : null}
          </div>
        </li>
      ))}
      {interesting.length > 100 ? (
        <li className="text-muted-foreground p-2 text-xs">
          …e altre {interesting.length - 100} righe.
        </li>
      ) : null}
    </ul>
  )
}
